/**
 * Task Extraction Loading Animation
 * 
 * Framer Motion animated skeleton that shows while the task-extractor
 * edge function is processing. Matches the CortexThinkingAnimation pattern.
 */

import React from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Brain } from 'lucide-react';
import { cn } from '@flowstate/utils';

interface TaskExtractionLoadingProps {
  isExtracting: boolean;
  className?: string;
}

export const TaskExtractionLoading: React.FC<TaskExtractionLoadingProps> = ({
  isExtracting,
  className,
}) => {
  return (
    <AnimatePresence>
      {isExtracting && (
        <motion.div
          initial={{ opacity: 0, height: 0, marginTop: 0 }}
          animate={{ opacity: 1, height: 'auto', marginTop: 12 }}
          exit={{ opacity: 0, height: 0, marginTop: 0 }}
          transition={{ duration: 0.3, ease: 'easeInOut' }}
          className={cn('overflow-hidden', className)}
        >
          <div className="pt-3 border-t border-border/30">
            <div className="rounded-xl p-3 bg-gradient-to-br from-primary/5 via-accent/5 to-primary/5 border border-primary/10">
              {/* Header */}
              <div className="flex items-center gap-2 mb-3">
                <motion.div
                  animate={{ rotate: [0, 360] }}
                  transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}
                >
                  <Brain className="h-4 w-4 text-primary" />
                </motion.div>
                <span className="text-xs font-medium text-primary">
                  Extracting action items...
                </span>
                <motion.div
                  className="flex space-x-1 ml-auto"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                >
                  {[0, 1, 2].map((i) => (
                    <motion.div
                      key={i}
                      className="w-1.5 h-1.5 rounded-full bg-primary"
                      animate={{ opacity: [1, 0.3, 1] }}
                      transition={{ duration: 1, repeat: Infinity, delay: i * 0.2 }}
                    />
                  ))}
                </motion.div>
              </div>

              {/* Skeleton task items */}
              <div className="space-y-2">
                {[0, 1, 2].map((i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: i * 0.15, duration: 0.3 }}
                    className="flex items-start gap-2.5"
                  >
                    {/* Checkbox skeleton */}
                    <motion.div
                      className="w-4 h-4 rounded-full border-2 border-primary/20 mt-0.5 flex-shrink-0"
                      animate={{ borderColor: ['rgba(var(--primary) / 0.2)', 'rgba(var(--primary) / 0.5)', 'rgba(var(--primary) / 0.2)'] }}
                      transition={{ duration: 1.5, repeat: Infinity, delay: i * 0.3 }}
                    />
                    {/* Text skeleton */}
                    <div className="flex-1 space-y-1.5">
                      <motion.div
                        className="h-3 rounded-md bg-muted"
                        style={{ width: `${75 - i * 12}%` }}
                        animate={{ opacity: [0.4, 0.8, 0.4] }}
                        transition={{ duration: 1.5, repeat: Infinity, delay: i * 0.2 }}
                      />
                      {/* Subtask skeletons for first item */}
                      {i === 0 && (
                        <div className="ml-4 space-y-1">
                          {[0, 1].map((j) => (
                            <motion.div
                              key={j}
                              className="h-2.5 rounded-md bg-muted/60"
                              style={{ width: `${55 - j * 10}%` }}
                              animate={{ opacity: [0.3, 0.6, 0.3] }}
                              transition={{ duration: 1.5, repeat: Infinity, delay: 0.5 + j * 0.2 }}
                            />
                          ))}
                        </div>
                      )}
                    </div>
                    {/* Priority badge skeleton */}
                    <motion.div
                      className="h-4 w-10 rounded-full bg-muted/50 flex-shrink-0"
                      animate={{ opacity: [0.3, 0.6, 0.3] }}
                      transition={{ duration: 1.5, repeat: Infinity, delay: i * 0.25 }}
                    />
                  </motion.div>
                ))}
              </div>

              {/* Progress bar */}
              <div className="relative h-1 mt-3 overflow-hidden rounded-full bg-muted">
                <motion.div
                  className="absolute inset-0 bg-gradient-to-r from-primary/50 via-accent/50 to-primary/50"
                  animate={{ x: ['-100%', '100%'] }}
                  transition={{ duration: 1.5, repeat: Infinity, ease: 'linear' }}
                />
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default TaskExtractionLoading;
